import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import { t } from "../contract/strings";
import { captureAnalytics } from "../analytics";
import { Skeleton, SkeletonGate, cx } from "../ui";
import { ChevronDownIcon, SendIcon, SparkleIcon, TrashIcon } from "../ui/icons";
import {
  CHAT_PROBLEM_KEY,
  chatBlocks,
  chatRows,
  keyboardInset,
  type ChatBlockRow,
  type ChatLimit,
  type ChatMessageRow,
  type KeyboardInset,
} from "./result-chat-model";
import type { MeetingChat } from "./result-chat-state";
import { FAIR_USE_HREF, PlansOverlay } from "./plan-picker";
import { nextPlan, planName, useWallet, type PlanKey, type Wallet } from "./wallet";
import { LABEL, fmtMinSec } from "./result-model";
import { say } from "./result-notes-model";

/**
 * Ask AI (#791): a person's private chat about the meeting, from a floating pill on every
 * tab of the result page.
 *
 * The pill sits bottom-right and toggles a card above it, 12 units up. On a phone (under
 * 640 wide) the card takes the **full width**, edge to edge with only its top corners
 * rounded, from the top safe area down to the pill; from 640 it is a **floating card**
 * 360 wide that runs to 16 units under the top of the screen (plus the safe areas). While
 * an on-screen keyboard is up, the whole chat sits **above it** (`useKeyboardInset`): the
 * question box right on the keyboard, the messages in the height left, the pill under the
 * keyboard. The chat is nonmodal: exposed page controls, scrolling and keyboard
 * navigation remain available. The pill stays beside the chat
 * and is the toggle (`aria-expanded`, `aria-controls`): open, it reads "HIDE" with a still
 * down chevron. Escape closes. Focus moves to the question box when it opens and back to
 * the pill when it closes. The card stays mounted while closed, so a draft survives, and
 * the answer being written lives in the screen's hook, so closing never stops it.
 *
 * Motion is `motion-safe` only: closed, the pill's sparkle turns once every 6 s with a soft
 * ring pulsing behind it; the card grows in from its bottom-right corner. Under `prefers-reduced-motion` all of it is off.
 */

export function AskAiButton({
  open,
  onToggle,
  buttonRef,
  controls,
}: {
  open: boolean;
  onToggle: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
  controls?: string;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      data-chat-open
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={open ? t("result.chat_hide_aria") : t("result.chat_open_aria")}
      onClick={onToggle}
      className={cx(
        "pointer-events-auto relative isolate flex-none",
        "inline-flex items-center gap-2 px-4 py-3 rounded-pill border-none cursor-pointer",
        "bg-accent text-accent-ink shadow-lg hover:brightness-90",
        // The tab labels' type: mono caps, bold, the name tracking, at 11 units.
        "font-code font-bold uppercase tracking-name text-stat-label leading-none",
        "transition-[transform,filter] duration-100 enabled:active:scale-95",
        "motion-reduce:transition-none motion-reduce:enabled:active:transform-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
      )}
    >
      {open ? (
        // Open: a still down chevron and HIDE, the same size and place -- nothing moves.
        <>
          <ChevronDownIcon
            data-chat-hide-glyph
            className="size-[1.125rem] text-accent-ink/90"
            aria-hidden="true"
          />
          <span>{t("result.chat_hide")}</span>
        </>
      ) : (
        <>
          {/* A ping behind the pill: a ring its own size, in the accent, growing outward
              and fading (`chat-ping`). Closed and motion only, so under reduced motion it is
              not drawn at all. Nothing on the pill clips it. */}
          <span
            aria-hidden="true"
            data-chat-ring
            className="pointer-events-none absolute inset-0 -z-10 rounded-pill bg-accent hidden motion-safe:block motion-safe:animate-chat-ping"
          />
          {/* The sparkle turns once every 6 s, and only where motion is welcome. */}
          <SparkleIcon
            data-chat-sparkle
            // On the accent fill: the accent ink a shade lighter, never `accent-soft`, which
            // is a background tint (8% accent) and vanishes on the pill.
            className="size-[1.125rem] text-accent-ink/90 motion-safe:animate-[spin_6s_linear_infinite]"
            aria-hidden="true"
          />
          <span>{t("result.chat_open")}</span>
        </>
      )}
    </button>
  );
}

/**
 * The pill and its card, fixed to the screen's bottom-right. The column spans the screen's
 * height between the safe areas but takes no pointer events itself, so the page under its
 * empty part stays usable; the card fills the height the pill leaves.
 */
export function AskAi({
  chat,
  open,
  onToggle,
  onClose,
  onJump,
  buttonRef,
}: {
  chat: MeetingChat;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onJump: (time: number) => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
}) {
  const cardId = `${useId()}-ask-ai`;
  const keyboard = useKeyboardInset(open);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (open && event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  }

  // Portalled for the same reason as `Overlay`: the screen's entrance leaves a transform
  // on an ancestor, which would pin a `fixed` child to the reading column.
  return createPortal(
    <div data-chat-layer onKeyDown={onKeyDown}>
      {/* A phone's column spans the width, from the top safe area; from 640 it is the
          floating column, 16 units in. With a keyboard up, the column's top and bottom
          follow the visual viewport instead, so the card ends right on the keyboard. */}
      <div
        data-chat-launcher
        data-keyboard={keyboard ? "" : undefined}
        style={
          keyboard
            ? { top: `calc(${keyboard.top}px + env(safe-area-inset-top, 0px))`, bottom: `${keyboard.bottom}px` }
            : undefined
        }
        className={cx(
          "fixed z-[35] inset-x-0 sm:left-auto sm:right-4 pointer-events-none flex flex-col items-end justify-end gap-3",
          "top-[env(safe-area-inset-top,0px)] sm:top-[calc(1rem+env(safe-area-inset-top,0px))]",
          "bottom-[calc(1rem+env(safe-area-inset-bottom,0px))]",
        )}
      >
        <ChatPanel id={cardId} chat={chat} open={open} onClose={onClose} onJump={onJump} squeezed={keyboard?.bottom ?? 0} />
        {/* The pill keeps its 16-unit inset on a phone, where the column is edge to edge.
            Under a keyboard it is out of the way: the card is the focus. */}
        <div data-chat-pill className={cx("flex-none max-sm:mr-4 pointer-events-none", keyboard ? "hidden" : "flex")}>
          <AskAiButton open={open} onToggle={onToggle} buttonRef={buttonRef} controls={cardId} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * While `active`, how much of the screen's bottom an on-screen keyboard covers (null when
 * none), from the VisualViewport's resize and scroll, measured once per frame.
 */
function useKeyboardInset(active: boolean): KeyboardInset | null {
  const [inset, setInset] = useState<KeyboardInset | null>(null);
  useEffect(() => {
    const viewport = typeof window === "undefined" ? undefined : window.visualViewport;
    // Null to start with, and the cleanup below resets it when the chat closes.
    if (!active || !viewport) return;
    // A flag, not the frame's handle: the handle is only known once the call returns.
    let pending = false;
    let frame = 0;
    const measure = () => {
      if (pending) return;
      pending = true;
      frame = requestAnimationFrame(() => {
        pending = false;
        const next = keyboardInset(window.innerHeight, viewport);
        setInset((prev) => (prev?.top === next?.top && prev?.bottom === next?.bottom ? prev : next));
      });
    };
    measure();
    viewport.addEventListener("resize", measure);
    viewport.addEventListener("scroll", measure);
    return () => {
      if (pending) cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", measure);
      viewport.removeEventListener("scroll", measure);
      setInset(null);
    };
  }, [active]);
  return inset;
}

function Cite({
  at,
  speaker,
  onJump,
}: {
  at: number;
  speaker: string | null;
  onJump: (time: number) => void;
}) {
  const time = fmtMinSec(at);
  const label = speaker
    ? t("result.chat_cite_aria", { time, name: speaker })
    : t("result.jump_to_moment_aria", { time });
  return (
    <button
      type="button"
      data-chat-cite
      onClick={() => onJump(at)}
      aria-label={label}
      title={label}
      className={cx(
        "relative inline ml-1 p-0 bg-transparent border-none cursor-pointer align-baseline",
        "font-medium text-accent underline underline-offset-3 whitespace-nowrap hover:brightness-90",
        // A 24-unit target around a line of small text, without moving the line.
        "after:absolute after:-inset-x-1 after:-inset-y-1.5 after:content-['']",
      )}
    >
      {speaker ? t("result.chat_cite", { name: speaker, time }) : t("result.jump_to_moment", { time })}
    </button>
  );
}

function Blocks({ blocks, onJump }: { blocks: ChatBlockRow[]; onJump: (time: number) => void }) {
  return (
    <>
      {blocks.map((block, index) => {
        const words = (
          <p className="m-0 whitespace-pre-wrap break-words">
            {block.pieces.map((piece, i) => (
              <span key={i}>
                {piece.kind === "quote" ? (
                  <q data-chat-quote className="italic">
                    {piece.text}
                  </q>
                ) : (
                  piece.text
                )}
                {piece.source_start !== null ? (
                  <Cite at={piece.source_start} speaker={piece.speaker} onJump={onJump} />
                ) : null}
              </span>
            ))}
          </p>
        );
        if (block.from_meeting) return <div key={index}>{words}</div>;
        return (
          <div
            key={index}
            data-chat-general
            className="rounded-control border border-dashed border-line-strong bg-surface px-2 py-1.5"
          >
            <span className={cx(LABEL, "block text-count text-ink-soft mb-1")}>
              {block.label ? say(block.label) : null}
            </span>
            {words}
          </div>
        );
      })}
    </>
  );
}

/** An assistant turn: the round sparkle avatar, then a tinted bubble squared at top-left. */
function FromAi({ children, ...data }: { children: ReactNode } & { [key: `data-${string}`]: boolean }) {
  return (
    <div className="flex items-start gap-2" {...data}>
      <span
        aria-hidden="true"
        className="size-6 mt-0.5 flex-none rounded-full bg-accent text-accent-ink flex items-center justify-center"
      >
        <SparkleIcon className="size-3.5" />
      </span>
      <div className="min-w-0 max-w-[85%] flex flex-col gap-2 p-2.5 rounded-card rounded-tl-none bg-surface-2 text-hint text-ink shadow-sm">
        <span className="sr-only">{t("result.chat_ai")}: </span>
        {children}
      </div>
    </div>
  );
}

/** A question: right-aligned, on the accent, squared at top-right. */
function FromYou({ text, ...data }: { text: string } & { [key: `data-${string}`]: boolean }) {
  return (
    <div className="flex justify-end" {...data}>
      <p className="m-0 max-w-[85%] p-2.5 rounded-card rounded-tr-none bg-accent text-accent-ink text-hint shadow-sm whitespace-pre-wrap break-words">
        <span className="sr-only">{t("result.chat_you")}: </span>
        {text}
      </p>
    </div>
  );
}

function Message({ row, onJump }: { row: ChatMessageRow; onJump: (time: number) => void }) {
  if (row.role === "user") {
    return (
      <li>
        <FromYou data-chat-question text={row.text} />
      </li>
    );
  }
  return (
    <li>
      <FromAi data-chat-answer>
        {row.refusal ? (
          <p data-chat-refusal className="m-0">
            {say(row.refusal)}
          </p>
        ) : (
          <Blocks blocks={row.blocks} onJump={onJump} />
        )}
      </FromAi>
    </li>
  );
}

/**
 * The history while it loads (#1043): a question on the right and an answer on the left,
 * in the bubbles' shapes, as static blocks.
 */
function ChatSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Skeleton className="w-3/5 h-tile-md rounded-card rounded-tr-none" />
      </div>
      <div className="flex items-start gap-2">
        <Skeleton className="size-6 mt-0.5 flex-none rounded-full" />
        <Skeleton className="w-4/5 h-tile-sm rounded-card rounded-tl-none" />
      </div>
    </div>
  );
}

/** The suggested questions. */
const SUGGESTIONS = [
  "result.chat_suggestion_commitments",
  "result.chat_suggestion_open",
  "result.chat_suggestion_follow_up",
] as const;

/** A header control: a 32-unit rounded square on the accent. */
const HEADER_BUTTON =
  "inline-flex items-center justify-center size-8 rounded-[0.5rem] border-none bg-transparent " +
  "text-accent-ink cursor-pointer hover:bg-accent-ink/15 focus-visible:outline-2 focus-visible:outline-accent-ink";

/**
 * A small action inside a bubble. The shape and the colours are separate strings: two
 * utilities for one property (`bg-surface` and `bg-danger`) are decided by Tailwind's emit
 * order, not by their order in the class list, so a variant never overrides the base's
 * colours -- it has its own complete set.
 */
const BUBBLE_SHAPE =
  "self-start inline-flex items-center min-h-8 px-3 rounded-pill border font-code text-count " +
  "font-bold uppercase tracking-name cursor-pointer";
const BUBBLE_BUTTON = `${BUBBLE_SHAPE} border-line-strong bg-surface text-ink hover:border-accent`;

/** Where a meeting's question limit points when the wallet can't be read: the plans. */
const PLANS_HREF = "/plans";

/**
 * A meeting's question limit, said with the way to more (#1010): "{n} questions used. {next
 * plan} includes {m} per meeting." The next plan is the one above the asker's: none ->
 * Basic -> Plus -> Pro, Pro's being unlimited. Null without a next plan, or before the
 * wallet's catalogue is read, where today's line stays.
 */
export function limitLine(limit: ChatLimit | null, wallet: Wallet | null): { line: string; next: PlanKey } | null {
  if (!limit || limit.limit == null || limit.plan === undefined || !wallet) return null;
  const next = nextPlan(limit.plan as PlanKey | null);
  const plan = next ? (wallet.plans ?? []).find((p) => p.plan === next) : undefined;
  if (!next || !plan) return null;
  const vars = { used: limit.limit, plan: planName(next) };
  return {
    next,
    line:
      plan.ask_ai_per_meeting == null
        ? t("result.chat_limit_next_unlimited", vars)
        : t("result.chat_limit_next", { ...vars, count: plan.ask_ai_per_meeting }),
  };
}

/** A line whose closing asterisk is Pro's fair use: the asterisk links to it, as Plans' does. */
function FairUseStar({ line }: { line: string }) {
  if (!line.endsWith("*")) return <>{line}</>;
  return (
    <>
      {line.slice(0, -1)}
      <a href={FAIR_USE_HREF} aria-label={t("plans.fair_use_link")} className="text-ink underline underline-offset-3"
        onClick={() => captureAnalytics("link_opened", { link: "fair_use" })}
      >
        *
      </a>
    </>
  );
}

/**
 * What a problem's line fills in: the question's length limit for most, the plan's
 * question limit for a meeting's, and the day Pro's fair use resets for the month's.
 */
function problemVars(chat: MeetingChat, max: number): Record<string, string | number> {
  if (chat.problem === "limit_meeting") return { max: chat.limit?.limit ?? max };
  if (chat.problem === "fair_use_month" && chat.limit?.resetsAt) {
    return {
      date: new Date(chat.limit.resetsAt).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
      }),
    };
  }
  return { max };
}
const DANGER_BUTTON = `${BUBBLE_SHAPE} border-transparent bg-danger text-accent-ink hover:brightness-90`;

/** How close to the limit a question gets before the count shows. */
const COUNT_FROM = 0.9;

/** The fixed failure categories the shared list allows (#1085), from the chat's problems. */
const FAILURE_CATEGORY = {
  load_failed: "request",
  rate_limited_soon: "rate_limited",
  rate_limited_today: "rate_limited",
  resting: "resting",
  busy: "request",
  full: "full",
  too_long: "request",
  error: "request",
  offline: "offline",
  delete_failed: "request",
  limit_meeting: "plan_limit",
  fair_use_month: "plan_limit",
} as const;

export function ChatPanel({
  id,
  chat,
  open = true,
  onClose,
  onJump,
  squeezed = 0,
}: {
  id?: string;
  chat: MeetingChat;
  open?: boolean;
  onClose: () => void;
  onJump: (time: number) => void;
  /** How much of the screen a keyboard covers: when it changes, the newest words stay in view. */
  squeezed?: number;
}) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const countId = `${ids}-count`;
  const card = useRef<HTMLElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState("");
  const [confirm, setConfirm] = useState<"delete" | "new" | null>(null);

  const rows = chat.thread ? chatRows(chat.thread) : null;
  const max = rows?.question_max_chars ?? 1000;
  const length = draft.trim().length;
  const tooLong = length > max;
  const nearLimit = length >= Math.floor(max * COUNT_FROM);
  const busy = chat.streaming !== null;
  const canType = Boolean(rows?.can_ask) && chat.online;
  // A plan's limit holds until the plan changes (#1010): the box keeps the draft, and
  // sending waits.
  const limited = chat.problem === "limit_meeting" || chat.problem === "fair_use_month";
  const canSend = canType && !busy && length > 0 && !tooLong && !limited;
  // The wallet's catalogue names the next plan's limit; read only once a limit is hit.
  const { wallet } = useWallet(chat.problem === "limit_meeting");
  const upgrade = chat.problem === "limit_meeting" ? limitLine(chat.limit, wallet) : null;
  const [plansOpen, setPlansOpen] = useState(false);
  const messages = rows?.messages ?? [];
  // Set by the first question sent, typed or tapped, and cleared only by deleting the chat.
  const [asked, setAsked] = useState(false);
  // The greeting follows the same rule: an empty chat only, gone from the first question
  // (while it is being answered too), back only once the chat is deleted. Never stored.
  const showGreeting = Boolean(rows) && messages.length === 0 && !busy && !asked;
  const showSuggestions = Boolean(rows?.can_ask) && showGreeting;

  // Focus moves into the question box (or the card) each time the card opens.
  useEffect(() => {
    if (!open) return;
    // preventScroll: the page behind must not move (iOS scrolls the document to a focused box).
    (input.current && !input.current.disabled ? input.current : card.current)?.focus({ preventScroll: true });
  }, [open]);

  // Keep the newest words in view as they arrive, and when a keyboard shrinks the list.
  // The list's own scroll, never scrollIntoView, which would scroll the page behind too.
  const streamed = chat.streaming?.segments.map((s) => s?.text ?? "").join("").length ?? 0;
  useEffect(() => {
    const el = list.current;
    if (open && el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length, streamed, busy, squeezed]);

  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    if (confirm) setConfirm(null);
    else onClose();
  }

  async function send(question: string): Promise<void> {
    const text = question.trim();
    if (!text || busy || !canType || limited || text.length > max) return;
    setDraft("");
    setAsked(true);
    captureAnalytics("ask_ai_question_asked", {});
    const problem = await chat.ask(text);
    if (problem) {
      captureAnalytics("ask_ai_failed", { category: FAILURE_CATEGORY[problem] });
    }
    // A question that was refused before it was asked goes back in the box; one that
    // failed while being answered is offered again with Try again instead.
    if (problem && problem !== "error") setDraft((d) => d || text);
  }

  async function confirmed(): Promise<void> {
    const action = confirm;
    setConfirm(null);
    if (await chat.remove()) {
      if (action) captureAnalytics("ask_ai_thread_cleared", { action });
      setConfirm(null);
      // An empty chat again: its suggestions come back.
      setAsked(false);
      input.current?.focus();
    }
  }

  const problemKey = !chat.online ? CHAT_PROBLEM_KEY.offline : chat.problem ? CHAT_PROBLEM_KEY[chat.problem] : null;
  // The day's rest and a full chat are said once, by the notice, not again as a problem.
  const notice = rows?.notice ? say(rows.notice) : null;
  const showProblem =
    problemKey && !(notice && (chat.problem === "resting" || chat.problem === "full"));

  return (
    <>
    {/* Outside the card, so the overlay's keys never reach the card's Escape. */}
    {plansOpen && wallet ? <PlansOverlay wallet={wallet} onClose={() => setPlansOpen(false)} /> : null}
    {open && confirm ? (
      <ChatConfirmation action={confirm} onCancel={() => setConfirm(null)} onConfirm={() => void confirmed()} />
    ) : null}
    <section
      ref={card}
      id={id}
      role="dialog"
      aria-label={t("result.chat_dialog_aria")}
      tabIndex={-1}
      data-chat-panel
      onKeyDown={onKeyDown}
      className={cx(
        open ? "flex" : "hidden",
        "pointer-events-auto flex-col flex-1 min-h-0 overflow-hidden outline-none",
        // A phone: the column's full width, square at the bottom. From 640: 360 wide, all
        // corners rounded.
        "w-full sm:w-[360px] rounded-t-card rounded-b-none sm:rounded-card bg-surface text-ink",
        "shadow-[0_12px_48px_8px_color-mix(in_srgb,var(--color-ink)_18%,transparent)]",
        "origin-bottom-right motion-safe:animate-chat-in",
      )}
    >
      {/* A div, not a <header>: app.css styles every `header` as the app's own top bar
          (a centred max width, the safe-area inset), which would shrink this one. */}
      <div
        data-chat-header
        className="flex-none w-full flex items-center justify-between gap-2 px-3.5 py-3 bg-accent text-accent-ink"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden="true"
            data-chat-tile
            className="size-7 flex-none rounded-[0.5rem] bg-accent-ink/15 flex items-center justify-center"
          >
            <SparkleIcon className="size-4 text-accent-ink/90" />
          </span>
          <div className="flex flex-col min-w-0">
            <h2 id={titleId} className="m-0 text-sm font-bold leading-tight">
              {t("result.chat_title")}
            </h2>
          </div>
        </div>
        {/* The pill is the only way to show and hide the card (Escape inside it closes
            it too), so the header holds one action: delete, once there is a chat. It waits
            while an answer is being written, which the server would refuse (busy). */}
        {messages.length > 0 ? (
          <button
            type="button"
            data-chat-delete
            aria-label={t("result.chat_delete")}
            title={t("result.chat_delete")}
            disabled={busy}
            onClick={() => setConfirm("delete")}
            className={cx(HEADER_BUTTON, "flex-none disabled:opacity-45 disabled:cursor-default")}
          >
            <TrashIcon className="size-[1.125rem] text-accent-ink/90" />
          </button>
        ) : null}
      </div>


      <div ref={list} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3.5 flex flex-col gap-3 bg-bg" data-chat-messages>

        {/* The history's first read is a skeleton of a question and its answer (#1043). On
            the web the pill appears only once that read has answered (`chatOffered`), so
            the panel never opens mid-read and this has no registry state; it is here for
            the day it can, and the apps' panels, which open first, draw it. */}
        <SkeletonGate loading={!rows && chat.loading} label={t("result.chat_loading")} skeleton={<ChatSkeleton />}>
          <ol className="list-none m-0 p-0 flex flex-col gap-3" aria-live="polite" aria-relevant="additions">
            {messages.map((row) => (
              <Message key={row.id} row={row} onJump={onJump} />
            ))}
          </ol>
        </SkeletonGate>
        {chat.streaming ? (
          <div className="flex flex-col gap-3" data-chat-streaming aria-busy="true">
            <FromYou text={chat.streaming.question} />
            {chat.streaming.inScope && chat.streaming.segments.length > 0 ? (
              <FromAi>
                <Blocks blocks={chatBlocks(chat.streaming.segments.filter(Boolean))} onJump={onJump} />
              </FromAi>
            ) : (
              <FromAi data-chat-typing>
                <p role="status" className="m-0 italic text-ink-soft">
                  {t("result.chat_writing")}
                </p>
              </FromAi>
            )}
          </div>
        ) : null}
        {notice ? (
          <FromAi data-chat-notice>
            <p role="status" className="m-0">
              {notice}
            </p>
            {rows?.can_restart && !confirm ? (
              <button type="button" data-chat-new onClick={() => setConfirm("new")} className={BUBBLE_BUTTON}>
                {t("result.chat_new")}
              </button>
            ) : null}
          </FromAi>
        ) : null}
        {showProblem ? (
          <FromAi data-chat-problem-bubble>
            <p role="alert" data-chat-problem className="m-0">
              {chat.online && upgrade ? <FairUseStar line={upgrade.line} /> : t(problemKey, problemVars(chat, max))}
            </p>
            {/* A meeting's question limit on the asker's plan (#874, #1010): the way to more
                is the next plan, so See plans opens the plans over this chat, which is
                still here when they close. Without the wallet, the plans' own page. */}
            {chat.online && chat.problem === "limit_meeting" && (upgrade || !wallet) ? (
              upgrade ? (
                <button type="button" data-chat-upgrade onClick={() => setPlansOpen(true)} className={BUBBLE_BUTTON}>
                  {t("plans.see")}
                </button>
              ) : (
                <a href={PLANS_HREF} data-chat-upgrade className={cx(BUBBLE_BUTTON, "no-underline")}>
                  {t("plans.see")}
                </a>
              )
            ) : null}
            {chat.online && chat.problem === "error" && chat.failedQuestion ? (
              <button type="button" onClick={() => {
                captureAnalytics("retry_tapped", { surface: "chat_reply" });
                chat.retry();
              }} className={BUBBLE_BUTTON}>
                {t("common.try_again")}
              </button>
            ) : null}
            {chat.online && chat.problem === "load_failed" ? (
              <button type="button" onClick={() => {
                captureAnalytics("retry_tapped", { surface: "chat_reply" });
                chat.load();
              }} className={BUBBLE_BUTTON}>
                {t("common.try_again")}
              </button>
            ) : null}
          </FromAi>
        ) : null}
      </div>

      {/* The suggestions, in thumb's reach just above the box, on an empty chat only: once
          a question is sent they are gone for this chat, back only after it is deleted. */}
      {showSuggestions ? (
        <div
          role="group"
          aria-label={t("result.chat_suggestions_aria")}
          data-chat-suggestions
          className="flex-none flex flex-wrap justify-end gap-x-2 px-2.5 pt-1 bg-bg"
        >
          {SUGGESTIONS.map((key) => (
            <button
              key={key}
              type="button"
              data-chat-suggestion
              disabled={!chat.online}
              onClick={() => void send(t(key))}
              // A 44-unit target around a smaller pill.
              className="group inline-flex items-center min-h-11 p-0 border-none bg-transparent cursor-pointer disabled:opacity-45 disabled:cursor-default"
            >
              <span
                className={cx(
                  "inline-flex items-center px-2.5 py-1 rounded-pill bg-surface text-ink",
                  "font-code text-label whitespace-nowrap shadow-sm group-enabled:group-hover:bg-surface-3",
                )}
              >
                {t(key)}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex-none p-2.5 bg-surface-2">
        <label htmlFor={`${ids}-input`} className="sr-only">
          {t("result.chat_input_label")}
        </label>
        <div className="flex items-center gap-2">
          <input
            ref={input}
            id={`${ids}-input`}
            type="text"
            data-chat-input
            value={draft}
            disabled={!canType}
            placeholder={t("result.chat_input_placeholder")}
            aria-describedby={nearLimit ? countId : undefined}
            aria-invalid={tooLong || undefined}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send(draft);
              }
            }}
            className={cx(
              // 16 px on a phone, so iOS does not zoom the page when the box takes focus.
              "flex-1 min-w-0 px-3 py-2 rounded-[0.5rem] border-none bg-surface text-base sm:text-hint text-ink shadow-sm",
              "placeholder:text-ink-soft disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-accent",
            )}
          />
          <button
            type="button"
            data-chat-send
            aria-label={t("result.chat_send")}
            disabled={!canSend}
            onClick={() => void send(draft)}
            className={cx(
              "size-9 flex-none inline-flex items-center justify-center rounded-[0.5rem] border-none cursor-pointer",
              "bg-accent text-accent-ink shadow-sm hover:brightness-90 disabled:opacity-45 disabled:cursor-default",
            )}
          >
            <SendIcon className="size-4" />
          </button>
        </div>
        {nearLimit ? (
          <p id={countId} className={cx("m-0 mt-1 text-count text-right", tooLong ? "text-danger" : "text-ink-soft")}>
            {tooLong ? t("result.chat_too_long", { max }) : t("result.chat_count", { count: length, max })}
          </p>
        ) : null}
      </div>
    </section>
    </>
  );
}

/** A top-layer dialog, separate from the nonmodal chat; the browser traps focus. */
function ChatConfirmation({ action, onCancel, onConfirm }: {
  action: "delete" | "new";
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    element?.showModal();
    cancel.current?.focus();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  return createPortal(
    <dialog
      ref={dialog}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-message`}
      data-chat-confirm
      onCancel={(event) => { event.preventDefault(); onCancel(); }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") { event.preventDefault(); onCancel(); }
      }}
      className="m-auto w-[calc(100%-2rem)] max-w-sm rounded-card border border-line bg-bg text-ink p-6 shadow-lg backdrop:bg-ink/20"
    >
      <h2 id={`${id}-title`} className="m-0 text-lg font-bold">
        {action === "delete" ? t("result.chat_delete") : t("result.chat_new")}
      </h2>
      <p id={`${id}-message`} className="my-4 text-hint">
        {action === "delete" ? t("result.chat_delete_confirm") : t("result.chat_new_confirm")}
      </p>
      <div className="flex flex-wrap justify-end gap-3">
        <button ref={cancel} type="button" onClick={onCancel} className={BUBBLE_BUTTON}>{t("common.cancel")}</button>
        <button type="button" data-chat-confirm-yes onClick={onConfirm} className={DANGER_BUTTON}>
          {action === "delete" ? t("result.chat_delete") : t("result.chat_new")}
        </button>
      </div>
    </dialog>, document.body,
  );
}
